import type { EntitlementStatus } from './types';
import { presentState, type BannerTone } from './present';

const TONE_CLASS: Record<Exclude<BannerTone, 'none'>, string> = {
  info: 'text-ink-700 bg-ink-50 border-ink-200',
  warning: 'text-amber-800 bg-amber-50 border-amber-300',
  restricted: 'text-orange-800 bg-orange-50 border-orange-300',
  locked: 'text-red-800 bg-red-50 border-red-300',
};

/**
 * License status banner (UIX-9). Reflects the enforcement state with a clear reason and a remediation
 * (recovery) path. Renders nothing in the Licensed state (normal operation = no banner). Advisory only;
 * the server is authoritative.
 */
export function LicenseStatusBanner({ status }: { status: EntitlementStatus }) {
  const p = presentState(status);
  if (p.tone === 'none') return <TrialCountdown trial={status.trial} />;

  return (
    <div role="status" aria-live="polite" className={`border rounded-sm px-4 py-3 text-sm ${TONE_CLASS[p.tone]}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold">{p.title}</span>
        <span className="text-xs uppercase tracking-wide opacity-70" data-testid="enforcement-state">
          {status.state}
        </span>
      </div>
      {p.message && <p className="mt-1">{p.message}</p>}
      {status.remediation && (
        <p className="mt-1 font-medium" data-testid="remediation">
          What to do: {status.remediation}
        </p>
      )}
      <TrialCountdown trial={status.trial} />
    </div>
  );
}

/** Trial countdown + conversion call-to-action (UIX-13). Renders nothing when there is no active trial. */
export function TrialCountdown({ trial }: { trial?: EntitlementStatus['trial'] }) {
  if (!trial || (!trial.active && !trial.expired)) return null;
  if (trial.expired) {
    return (
      <p className="mt-2 text-sm text-orange-800" data-testid="trial">
        Your trial has ended.{trial.conversion ? ` ${trial.conversion}` : ''}
      </p>
    );
  }
  const days = trial.secondsRemaining !== undefined ? Math.max(0, Math.floor(trial.secondsRemaining / 86_400)) : undefined;
  return (
    <p className="mt-2 text-sm text-ink-700" data-testid="trial">
      Trial active{days !== undefined ? ` — ${days} day${days === 1 ? '' : 's'} remaining` : ''}.
      {trial.conversion ? ` ${trial.conversion}` : ''}
    </p>
  );
}
